import React from "react";
import CustomTooltip from "../../../../code-connect/component/CustomTooltip";
import { ActionTrashIcon, RailIconFO, TruckIconFO } from "../../../../icons";
import { icons } from "../../../../svg";
import {
  IFavoriteLocationExtension,
  ModeValueRow,
  MonthColumn,
  MonthHalf,
  OriginTypeFilter,
} from "../../../../types/IFavoriteOrigins";
import { DragState, SectionKey } from "../../../../types/IOriginManager";
import { getRowKey } from "../FavoriteOriginHelper";
import { CompetitorIndicationsInfoRow } from "./OriginManagerInfoRows";
import PriceCell, { PriceCellProps } from "./PriceCell";

type ColumnWithSpan = MonthColumn & { colSpan?: number };

type PassthroughPriceCellProps = Pick<
  PriceCellProps,
  | "isEditingPrices"
  | "activeCellKey"
  | "recentlySavedKeys"
  | "dragHoverPriceKey"
  | "draftPrices"
  | "draftPriceShowAs"
  | "isNewlyAddedOrigin"
  | "isPriceDragCopying"
  | "priceInputRefs"
  | "getPriceInputKey"
  | "getPriceInputValue"
  | "getPriceShowAsValue"
  | "getDisplayPriceValue"
  | "handlePriceCellClick"
  | "handlePriceInputChange"
  | "handlePriceInputKeyDown"
  | "handlePriceInputBlur"
  | "handlePriceShowAsChange"
  | "handlePriceShowAsChangeAndSave"
  | "handleToggleSplitMonth"
  | "handleRevertEditingPrices"
  | "applyDraggedPriceToInput"
  | "startPriceDragCopy"
>;

export interface OriginRowProps extends PassthroughPriceCellProps {
  origin: IFavoriteLocationExtension;
  index: number;
  filteredRows: IFavoriteLocationExtension[];
  rows: IFavoriteLocationExtension[];
  newlyAddedOrigins: Record<string, IFavoriteLocationExtension[]>;
  originTypeFilter: OriginTypeFilter;
  selectedMonths: string[];
  activeSplitMonths: string[];
  getSplitMonthsForRow: (origin: IFavoriteLocationExtension) => string[];
  getModeRows: (
    origin: IFavoriteLocationExtension,
    monthChip?: string,
    half?: MonthHalf,
  ) => ModeValueRow[];
  editingProductKey: string | null;
  editingRowKey: string | null;
  selectedProduct: string;
  dragOverRowKey: string | null;
  dragState: DragState | null;
  monthGridTemplateColumns: string;
  registerMonthScroller: (key: string) => (node: HTMLDivElement | null) => void;
  onMonthScroll: (sourceKey: string, scrollLeft: number) => void;
  // Lets the panel capture this row's DOM node so it can scroll the row into view when editing starts.
  registerRowRef: (key: string) => (node: HTMLDivElement | null) => void;
  handleDragStart: (
    event: React.DragEvent<HTMLButtonElement>,
    rowKey: string,
    section: SectionKey,
  ) => void;
  handleDragEnd: () => void;
  handleRowDragOver: (
    event: React.DragEvent<HTMLDivElement>,
    rowKey: string,
    section: SectionKey,
  ) => void;
  handleRowDrop: (
    event: React.DragEvent<HTMLDivElement>,
    rowKey: string,
    section: SectionKey,
    rows: IFavoriteLocationExtension[],
  ) => void;
  openInsightsPanel: (
    locationId: string,
    tab: "Facility" | "Occupants" | "Logistics",
    editing?: boolean,
  ) => void;
  onCopyToClipboard: (origin: IFavoriteLocationExtension) => void;
  onRequestDelete: (origin: IFavoriteLocationExtension) => void;
}

// A single origin's row: drag handle, name/mode labels, the scrollable price
// grid (rendered via PriceCell), and the row's copy/delete actions. Also
// renders the "competitor indications" banner above the first competitor row.
const OriginRow: React.FC<OriginRowProps> = ({
  origin,
  index,
  filteredRows,
  rows,
  newlyAddedOrigins,
  originTypeFilter,
  selectedMonths,
  activeSplitMonths,
  getSplitMonthsForRow,
  getModeRows,
  isEditingPrices,
  editingProductKey,
  editingRowKey,
  selectedProduct,
  dragOverRowKey,
  dragState,
  monthGridTemplateColumns,
  registerMonthScroller,
  onMonthScroll,
  registerRowRef,
  handleDragStart,
  handleDragEnd,
  handleRowDragOver,
  handleRowDrop,
  openInsightsPanel,
  onCopyToClipboard,
  onRequestDelete,
  ...priceCellProps
}) => {
  const modeRows = getModeRows(origin);
  const rowKey = getRowKey(origin);
  const isNewlyAddedOrigin = (newlyAddedOrigins[selectedProduct] ?? []).some(
    (candidate) => getRowKey(candidate) === rowKey,
  );
  const showCompetitorBanner =
    (originTypeFilter === "all" || originTypeFilter === "competitor") &&
    origin.competitor &&
    (index === 0 || !filteredRows[index - 1].competitor);
  const rowSplitMonths = new Set(getSplitMonthsForRow(origin));
  const rowColumns = selectedMonths.reduce<ColumnWithSpan[]>((acc, month) => {
    const monthIsSplitInHeader = activeSplitMonths.includes(month);
    const monthIsSplitForRow = rowSplitMonths.has(month);

    if (!monthIsSplitInHeader) {
      acc.push({ month, half: "full", isSplit: false });
      return acc;
    }

    if (monthIsSplitForRow) {
      acc.push(
        { month, half: "h1", isSplit: true },
        { month, half: "h2", isSplit: true },
      );
      return acc;
    }

    acc.push({ month, half: "full", isSplit: false, colSpan: 2 });
    return acc;
  }, []);
  const isEditingCurrentProduct =
    isEditingPrices && editingProductKey === selectedProduct;
  const isEditingCompetitorSection =
    isEditingCurrentProduct &&
    typeof editingRowKey === "string" &&
    editingRowKey.startsWith("competitor-");
  const isRowEditing =
    isEditingCurrentProduct &&
    (editingRowKey === rowKey ||
      (isEditingCompetitorSection && origin.competitor));

  return (
    <>
      {showCompetitorBanner && (
        <CompetitorIndicationsInfoRow
          rowKey={rowKey}
          monthGridTemplateColumns={monthGridTemplateColumns}
          registerMonthScroller={registerMonthScroller}
          onMonthScroll={onMonthScroll}
        />
      )}
      <div
        // Registered by rowKey so the panel can scroll this exact row into view when it enters edit mode.
        ref={registerRowRef(rowKey)}
        className={`dom-pane-row dom-origin-group${
          index === 0 ? " dom-origin-group--first" : ""
        }${isRowEditing ? " dom-origin-group--editing" : ""}${
          dragOverRowKey === rowKey ? " dom-origin-group--drag-over" : ""
        }`}
        onDragOver={(event) =>
          handleRowDragOver(
            event,
            rowKey,
            origin.competitor ? "competitor" : "koch",
          )
        }
        onDrop={(event) =>
          handleRowDrop(
            event,
            rowKey,
            origin.competitor ? "competitor" : "koch",
            rows,
          )
        }
      >
        <div className="dom-pane-left dom-pane-left--body">
          <div className="dom-cell drag group-cell">
            <CustomTooltip
              title="Drag to reorder"
              variant="dark"
              placement="bottom-start"
              arrow
            >
              <span className="dom-action-tooltip-trigger">
                <button
                  id={`origin-${origin.location.id}-drag`}
                  type="button"
                  className={`dom-drag-glyph${
                    dragState?.rowKey === rowKey
                      ? " dom-drag-glyph--active"
                      : ""
                  }`}
                  aria-label="Reorder origin"
                  draggable
                  onDragStart={(event) =>
                    handleDragStart(
                      event,
                      rowKey,
                      origin.competitor ? "competitor" : "koch",
                    )
                  }
                  onDragEnd={handleDragEnd}
                >
                  {Array.from({ length: 6 }, (_, i) => (
                    <span
                      key={`${rowKey}-drag-dot-${i}`}
                      className="dom-drag-dot"
                    />
                  ))}
                </button>
              </span>
            </CustomTooltip>
          </div>

          <div className="dom-cell origin group-cell origin-name-cell">
            <button
              id={`origin-${origin.location.id}-link`}
              type="button"
              className={`dom-origin-link ${
                origin.competitor ? "dom-origin-link-competitor" : ""
              }`}
              onClick={() =>
                openInsightsPanel(origin.location.id, "Logistics", false)
              }
            >
              {origin.location.name}
            </button>
          </div>

          <div className="dom-cell mode group-cell">
            {modeRows.map((modeRow) => (
              <div
                className={`dom-mode-line ${
                  isRowEditing ? "dom-mode-line-editable" : ""
                }`}
                key={modeRow.key}
              >
                {modeRow.icon === "truck" && (
                  <TruckIconFO size="20" color="#60a5fa" />
                )}
                {modeRow.icon === "rail" && (
                  <RailIconFO size="20" color="#f97316" />
                )}
                <span>{modeRow.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div
          ref={registerMonthScroller(`row-${rowKey}`)}
          className="dom-pane-center-scroll dom-pane-center-scroll--body"
          onScroll={(event) =>
            onMonthScroll(`row-${rowKey}`, event.currentTarget.scrollLeft)
          }
        >
          <div
            className="dom-pane-month-grid"
            style={{ gridTemplateColumns: monthGridTemplateColumns }}
          >
            {rowColumns.map((column) => {
              const monthModeRows = getModeRows(
                origin,
                column.month,
                column.half,
              );
              return (
                <div
                  key={`${column.month}-${column.half}`}
                  className="dom-cell month group-cell"
                  style={
                    column.colSpan
                      ? { gridColumn: `span ${column.colSpan}` }
                      : undefined
                  }
                >
                  {monthModeRows.map((modeRow) => (
                    <PriceCell
                      key={`${modeRow.key}-${column.month}-${column.half}`}
                      origin={origin}
                      modeRow={modeRow}
                      column={column}
                      rowColumns={rowColumns}
                      monthModeRows={monthModeRows}
                      isRowEditing={isRowEditing}
                      isEditingPrices={isEditingPrices}
                      isNewlyAddedOrigin={isNewlyAddedOrigin}
                      {...priceCellProps}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        <div className="dom-pane-right dom-pane-right--body">
          <CustomTooltip
            title="Copy to Clipboard"
            variant="dark"
            placement="bottom-start"
            arrow
          >
            <span className="dom-action-tooltip-trigger">
              <button
                id="copy-to-clipboard-btn"
                type="button"
                className="dom-cell action-btn dom-copy-btn"
                aria-label="Copy to Clipboard"
                onClick={() => onCopyToClipboard(origin)}
              >
                <span className="dom-copy-icon-wrap">
                  <img
                    src={icons.ContentPasteOutlinedIcon}
                    alt="content-paste"
                  />
                </span>
              </button>
            </span>
          </CustomTooltip>

          <CustomTooltip
            title="Remove Origin"
            variant="dark"
            placement="bottom-start"
            arrow
          >
            <span className="dom-action-tooltip-trigger">
              <button
                id="delete-origin-btn"
                type="button"
                className="dom-cell action-btn dom-delete-btn"
                aria-label="Delete"
                onClick={() => onRequestDelete(origin)}
              >
                <span className="dom-trash-icon">
                  <ActionTrashIcon />
                </span>
              </button>
            </span>
          </CustomTooltip>
        </div>
      </div>
    </>
  );
};

export default OriginRow;
